import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip } from '@mui/material';
import { ChevronRight as ChevronIcon } from '@mui/icons-material';
import type { HelpTopic } from '../../help/types';
import { appColors } from '../../Config/Theme';
import HelpRoleChips from './HelpRoleChips';

interface HelpTopicCardProps {
  topic: HelpTopic;
}

const categoryColorMap: Record<string, string> = {
  'Quick Start': appColors.primary.main,
  'Core Workflow': appColors.primary.accent,
  'Reference': appColors.status.success,
  'Administration': appColors.status.accent,
  'Troubleshooting': appColors.status.warning,
};

const HelpTopicCard: React.FC<HelpTopicCardProps> = ({ topic }) => {
  const navigate = useNavigate();
  const catColor = categoryColorMap[topic.category] || appColors.text.caption;

  return (
    <Box
      onClick={() => navigate(`/help/${topic.slug}`)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/help/${topic.slug}`); } }}
      sx={{
        bgcolor: appColors.surface.card,
        border: `1px solid ${appColors.border.default}`,
        borderRadius: '12px',
        px: 2.5,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': {
          borderColor: appColors.primary.main,
          boxShadow: appColors.shadow.cardHover,
        },
        '&:focus-visible': {
          outline: `2px solid ${appColors.primary.main}`,
          outlineOffset: 2,
        },
        minHeight: 120,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Chip
          label={topic.category}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.65rem',
            height: 20,
            borderRadius: '10px',
            bgcolor: `${catColor}14`,
            color: catColor,
          }}
        />
        <ChevronIcon sx={{ color: appColors.text.caption, fontSize: 20 }} />
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, lineHeight: 1.3 }}>
        {topic.title}
      </Typography>
      <Typography variant="body2" sx={{ color: appColors.text.body, lineHeight: 1.5, fontSize: '0.8rem', flex: 1 }}>
        {topic.summary}
      </Typography>
      <HelpRoleChips audience={topic.audience} />
    </Box>
  );
};

export default HelpTopicCard;
